import React, { useState } from 'react';
import { Link } from 'react-router-dom';

type Feature = {
  icon: string;
  title: string;
  description: string;
  detail: string;
  screenshot: string | null; // path relative to /public, or null for styled preview
  previewBg: string;
  previewContent: React.ReactNode;
};

/** Styled mini-UI previews rendered inside the modal when no screenshot is set */
const ReviewPreview = () => (
  <div className="space-y-2 text-xs">
    <div className="flex items-center gap-2 mb-3">
      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium text-[11px]">In progress</span>
      <span className="text-gray-400">2024–25 Annual Review · Tom Bradley</span>
    </div>
    {['Raise Year 9 progress by 10%', 'Lead curriculum review for KS4', 'Complete NPQ Senco module'].map((t, i) => (
      <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-white border border-gray-200">
        <div className={`mt-0.5 w-3 h-3 rounded-full flex-shrink-0 ${i === 0 ? 'bg-green-400' : i === 1 ? 'bg-amber-400' : 'bg-gray-200'}`} />
        <span className="text-gray-700">{t}</span>
      </div>
    ))}
    <div className="mt-3 flex gap-2">
      <div className="flex-1 h-7 rounded bg-brand-600 flex items-center justify-center text-white text-[10px] font-medium">Submit self-review</div>
    </div>
  </div>
);

const CpdPreview = () => (
  <div className="space-y-2 text-xs">
    {[
      { type: 'Course', title: 'Safeguarding Level 2', date: 'Mar 2025', hours: 6 },
      { type: 'INSET',  title: 'Curriculum planning day', date: 'Feb 2025', hours: 7.5 },
      { type: 'Webinar', title: 'Trauma-informed practice', date: 'Jan 2025', hours: 2 },
    ].map((e, i) => (
      <div key={i} className="p-2 rounded-lg bg-white border border-gray-200 flex items-center justify-between">
        <div>
          <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-medium mr-2">{e.type}</span>
          <span className="text-gray-800">{e.title}</span>
          <div className="text-gray-400 text-[10px] mt-0.5">{e.date}</div>
        </div>
        <span className="text-gray-500 text-[11px] font-medium whitespace-nowrap">{e.hours}h</span>
      </div>
    ))}
    <div className="text-right text-[11px] text-gray-500 font-medium">Total CPD this year: <strong className="text-brand-700">15.5 hrs</strong></div>
  </div>
);

const RemindersPreview = () => (
  <div className="space-y-3 text-xs">
    <div className="p-3 rounded-lg bg-white border border-gray-200">
      <div className="font-semibold text-gray-800 mb-1">📧 Staff deadline reminder</div>
      <p className="text-gray-500 text-[11px]">Sent automatically 7 days before the self-review deadline. Includes a direct link to the review.</p>
    </div>
    <div className="p-3 rounded-lg bg-white border border-gray-200">
      <div className="font-semibold text-gray-800 mb-1">📧 Line manager reminder</div>
      <p className="text-gray-500 text-[11px]">Sent automatically before the line manager confirmation deadline. Includes a list of staff awaiting sign-off.</p>
    </div>
    <div className="mt-2 p-2 rounded bg-green-50 border border-green-200 text-green-700 text-[11px]">
      ✓ All reminders sent automatically — no admin action needed
    </div>
  </div>
);

const PdfPreview = () => (
  <div className="text-xs space-y-2">
    <div className="bg-white border border-gray-300 rounded-lg p-3 shadow-sm">
      <div className="font-bold text-gray-900 text-sm border-b border-gray-200 pb-2 mb-2">Performance Review 2024–25</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-600 mb-2">
        <div><span className="font-medium text-gray-700">Staff member:</span> Tom Bradley</div>
        <div><span className="font-medium text-gray-700">Role:</span> Teacher (Form Tutor)</div>
        <div><span className="font-medium text-gray-700">Reviewer:</span> Claire Hanson</div>
        <div><span className="font-medium text-gray-700">Status:</span> <span className="text-green-600 font-medium">Confirmed</span></div>
      </div>
      <div className="space-y-1">
        {['Objective 1 · Met', 'Objective 2 · Partially met', 'Objective 3 · Met'].map((o, i) => (
          <div key={i} className="text-[11px] flex justify-between border-b border-gray-100 pb-1">
            <span className="text-gray-700">{o.split(' · ')[0]}</span>
            <span className={`font-medium ${o.includes('Partially') ? 'text-amber-600' : 'text-green-600'}`}>{o.split(' · ')[1]}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="text-center text-[10px] text-gray-400">Print-ready · No formatting needed</div>
  </div>
);

const PayPreview = () => (
  <div className="text-xs space-y-2">
    <div className="grid grid-cols-3 gap-1 text-[11px] font-medium text-gray-500 border-b border-gray-200 pb-1">
      <span>Staff member</span><span>Recommendation</span><span>Pay point</span>
    </div>
    {[
      { name: 'Tom Bradley',   rec: 'Recommend',    pay: 'M3 → M4', colour: 'text-green-600' },
      { name: 'Laura Singh',   rec: 'Recommend',    pay: 'M5 → M6', colour: 'text-green-600' },
      { name: 'David Okafor',  rec: 'Not recommend', pay: 'UPS2',   colour: 'text-red-500' },
    ].map((r, i) => (
      <div key={i} className="grid grid-cols-3 gap-1 text-[11px] items-center py-1 border-b border-gray-100">
        <span className="font-medium text-gray-800">{r.name}</span>
        <span className={r.colour + ' font-medium'}>{r.rec}</span>
        <span className="text-gray-500">{r.pay}</span>
      </div>
    ))}
    <div className="mt-2 flex gap-2">
      <div className="flex-1 h-7 rounded bg-white border border-gray-300 flex items-center justify-center text-gray-600 text-[10px]">Export CSV</div>
    </div>
  </div>
);

const GdprPreview = () => (
  <div className="text-xs space-y-2">
    {[
      { icon: '🇬🇧', label: 'UK data storage', desc: "Google's London data centre (europe-west2)" },
      { icon: '📋', label: 'Full audit log',  desc: 'Every action timestamped and attributed' },
      { icon: '🗑️', label: 'Data purge tools', desc: 'Remove any staff member\'s data on request' },
      { icon: '📄', label: 'DPA included',    desc: 'Data Processing Agreement supplied as standard' },
    ].map((item, i) => (
      <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-white border border-gray-200">
        <span className="text-base">{item.icon}</span>
        <div>
          <div className="font-medium text-gray-800">{item.label}</div>
          <div className="text-gray-500 text-[11px]">{item.desc}</div>
        </div>
      </div>
    ))}
  </div>
);

const features: Feature[] = [
  {
    icon: '📋',
    title: 'Full review workflow',
    description:
      'Self-review, line manager confirmation, and mid-year check-in — built around how your school actually runs appraisals.',
    detail:
      'Staff set their own objectives, complete their self-review, and submit when ready. Line managers rate performance, record strengths and development areas, and make pay progression decisions — all in one place. A mid-year check-in keeps everyone aligned throughout the year.',
    screenshot: null,
    previewBg: 'from-blue-50 to-indigo-50',
    previewContent: <ReviewPreview />,
  },
  {
    icon: '📚',
    title: 'CPD log & evidence linking',
    description:
      'Staff record CPD — courses, INSETs, webinars, certificates — and attach entries directly to their review objectives as evidence. Everything in one place, no chasing paperwork.',
    detail:
      'Every CPD entry is timestamped and categorised. Staff can link CPD activities directly to their review objectives as evidence. Line managers see the full picture at review time — no paperwork trail to follow.',
    screenshot: null,
    previewBg: 'from-indigo-50 to-purple-50',
    previewContent: <CpdPreview />,
  },
  {
    icon: '⏰',
    title: 'Automated reminders',
    description:
      'Deadline reminder emails sent automatically to staff and line managers so nothing slips through.',
    detail:
      'Set your deadlines once when you create the review cycle. PM Review sends reminder emails automatically as deadlines approach — no need for admin to chase individuals. Reminders go to the right person at the right time.',
    screenshot: null,
    previewBg: 'from-amber-50 to-orange-50',
    previewContent: <RemindersPreview />,
  },
  {
    icon: '📄',
    title: 'PDF export',
    description:
      'Print-ready reviews for Ofsted, governors, and HR records — one click, no formatting required.',
    detail:
      'Every completed review can be exported to a clean, formatted PDF instantly. Suitable for personnel files, governor reports, and Ofsted evidence packs. No copying, no reformatting — just click print.',
    screenshot: null,
    previewBg: 'from-slate-50 to-gray-100',
    previewContent: <PdfPreview />,
  },
  {
    icon: '📈',
    title: 'Pay progression tracking',
    description:
      'Built-in pay progression decisions with CSV export. Designed around the way schools manage their pay spine.',
    detail:
      'Line managers record pay progression recommendations as part of the review sign-off. Admins get a whole-school summary view and can export to CSV for payroll. The full recommendation history is stored against each staff member\'s record.',
    screenshot: null,
    previewBg: 'from-green-50 to-teal-50',
    previewContent: <PayPreview />,
  },
  {
    icon: '🔒',
    title: 'GDPR compliant',
    description:
      'All data stored in the UK. Full audit log, built-in data purge tools, and a data processing agreement included as standard.',
    detail:
      'Built from the ground up with UK schools\' data obligations in mind. Data is stored exclusively in Google\'s London data centre. Every action is logged with a timestamp and user attribution. Staff data can be purged at any time. A signed Data Processing Agreement is provided as standard.',
    screenshot: null,
    previewBg: 'from-rose-50 to-pink-50',
    previewContent: <GdprPreview />,
  },
];

const trustItems = [
  {
    icon: '🇬🇧',
    title: 'UK data storage',
    description:
      "Hosted in Google's London data centre. Your data never leaves the UK.",
  },
  {
    icon: '📜',
    title: 'GDPR compliant',
    description:
      'Full data processing agreement included as standard. Built-in data purge tools and a complete audit log.',
  },
  {
    icon: '📤',
    title: 'Your data, always',
    description: 'Export your data at any time. No lock-in, no barriers to leaving.',
  },
];

export default function LandingPage() {
  const [activeFeature, setActiveFeature] = useState<Feature | null>(null);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <span className="text-xl font-bold text-brand-900">PM Review</span>
          <div className="flex items-center gap-6">
            <a
              href="#pricing"
              className="text-sm text-gray-600 hover:text-brand-600 transition-colors"
            >
              Pricing
            </a>
            <Link
              to="/faq"
              className="text-sm text-gray-600 hover:text-brand-600 transition-colors"
            >
              FAQ
            </Link>
            <Link
              to="/data-security"
              className="text-sm text-gray-600 hover:text-brand-600 transition-colors"
            >
              Data &amp; Security
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-slate-50 border-b border-gray-100">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-600 mb-4">
            Built for UK schools
          </p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-brand-900 leading-tight mb-6">
            Performance management software,
            <br className="hidden sm:block" /> built around your school
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-6 leading-relaxed">
            Not an off-the-shelf product that schools have to bend themselves around.
            PM Review is built to fit the way your school already works — your processes,
            your cycle, your staff.
          </p>
          <p className="text-base text-gray-500 max-w-xl mx-auto mb-10">
            We work with a small number of schools directly. If you'd like to find out
            whether PM Review is the right fit for you, get in touch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:hello@pmreview.co.uk?subject=PM Review enquiry"
              className="inline-flex items-center justify-center rounded-md bg-brand-600 px-6 py-3 text-base font-semibold text-white hover:bg-brand-700 transition-colors shadow-sm"
            >
              Get in touch
            </a>
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-md border border-brand-600 bg-white px-6 py-3 text-base font-semibold text-brand-600 hover:bg-brand-50 transition-colors"
            >
              Sign in →
            </Link>
          </div>
        </div>
      </section>

      {/* Why bespoke */}
      <section className="py-20 border-b border-gray-100">
        <div className="mx-auto max-w-4xl px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 text-center">
            <div>
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="font-semibold text-gray-900 mb-2">Built around your school</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Your appraisal cycle, your objectives, your process — not a generic template
                that doesn't quite fit.
              </p>
            </div>
            <div>
              <div className="text-3xl mb-3">🤝</div>
              <h3 className="font-semibold text-gray-900 mb-2">Direct support</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                You deal with the developer directly. No call centres, no ticket queues.
                Questions answered within 24 hours.
              </p>
            </div>
            <div>
              <div className="text-3xl mb-3">⚡</div>
              <h3 className="font-semibold text-gray-900 mb-2">Changes when you need them</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                If something doesn't work the way you need it to, we can change it.
                Not possible with off-the-shelf software.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="py-20 bg-slate-50 border-y border-gray-100">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-900 text-center mb-4">
            See it in action
          </h2>
          <p className="text-center text-gray-500 text-sm mb-14 max-w-xl mx-auto">
            Real screenshots from the app — no mock-ups.
          </p>

          <div className="space-y-16">
            {/* Screenshot 1 — Cycle dashboard */}
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1 lg:order-2">
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                  <img
                    src="/screenshots/cycle-overview.png"
                    alt="Cycle progress dashboard showing completion stats, progress bar and objective rating distribution"
                    className="w-full h-auto block"
                  />
                </div>
              </div>
              <div className="flex-shrink-0 lg:w-72 lg:order-1">
                <p className="text-xs font-semibold uppercase tracking-widest text-brand-600 mb-2">School admin</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Full visibility at a glance</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  See every review's status in one place — who's on track, who needs a nudge,
                  and how objectives are being rated across the whole cycle. No chasing spreadsheets.
                </p>
              </div>
            </div>

            {/* Screenshot 2 — Staff review form */}
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1">
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                  <img
                    src="/screenshots/review-form.png"
                    alt="Staff self-review form showing objectives and evidence notes"
                    className="w-full h-auto block"
                  />
                </div>
              </div>
              <div className="flex-shrink-0 lg:w-72">
                <p className="text-xs font-semibold uppercase tracking-widest text-brand-600 mb-2">Staff view</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Clear for staff to complete</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Staff see their objectives, write their evidence, and submit — all in one clean form.
                  No confusion about what's expected or where to find it.
                </p>
              </div>
            </div>

            {/* Screenshot 3 — CPD log */}
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1 lg:order-2">
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                  <img
                    src="/screenshots/cpd-log.png"
                    alt="CPD log showing staff professional development entries with dates and descriptions"
                    className="w-full h-auto block"
                  />
                </div>
              </div>
              <div className="flex-shrink-0 lg:w-72 lg:order-1">
                <p className="text-xs font-semibold uppercase tracking-widest text-brand-600 mb-2">CPD log</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">CPD recorded and linked to targets</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Staff log their continuing professional development — courses, INSETs, webinars,
                  certificates — and attach individual entries directly to their review objectives as evidence.
                  No more tracking CPD separately from appraisals.
                </p>
              </div>
            </div>

            {/* Screenshot 4 — Cycles list */}
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1">
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                  <img
                    src="/screenshots/cycles-page.png"
                    alt="Cycles page showing active appraisal cycle with deadlines"
                    className="w-full h-auto block"
                  />
                </div>
              </div>
              <div className="flex-shrink-0 lg:w-72">
                <p className="text-xs font-semibold uppercase tracking-widest text-brand-600 mb-2">Cycle management</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Deadlines tracked automatically</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Staff and line manager deadlines are set once and tracked throughout the cycle.
                  Reminder emails go out automatically — no manual chasing required.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-900 text-center mb-4">
            Everything your school needs
          </h2>
          <p className="text-center text-gray-500 text-sm mb-12 max-w-xl mx-auto">
            A complete performance management system — from the first self-review
            to mid-year check-in, pay progression, and Ofsted-ready PDF exports.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f) => (
              <button
                key={f.title}
                type="button"
                onClick={() => setActiveFeature(f)}
                className="rounded-xl border border-gray-200 bg-white p-6 hover:shadow-md hover:border-brand-300 transition-all text-left group cursor-pointer"
              >
                <div className="text-3xl mb-3">{f.icon}</div>
                <h3 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-brand-700 transition-colors">
                  {f.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {f.description}
                </p>
                <div className="mt-3 text-xs font-medium text-brand-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  See how it works →
                </div>
              </button>
            ))}
          </div>

          {/* Feature modal / lightbox */}
          {activeFeature && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
              onClick={() => setActiveFeature(null)}
            >
              <div
                className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className={`bg-gradient-to-br ${activeFeature.previewBg} px-6 pt-6 pb-4 border-b border-gray-100`}>
                  <button
                    type="button"
                    onClick={() => setActiveFeature(null)}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="text-4xl mb-3">{activeFeature.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{activeFeature.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed max-w-lg">{activeFeature.detail}</p>
                </div>

                {/* Screenshot or styled preview */}
                <div className={`bg-gradient-to-br ${activeFeature.previewBg} px-6 py-5`}>
                  {activeFeature.screenshot ? (
                    <img
                      src={activeFeature.screenshot}
                      alt={`${activeFeature.title} screenshot`}
                      className="rounded-xl border border-gray-200 shadow-md w-full"
                    />
                  ) : (
                    <div className="rounded-xl border border-gray-200 bg-white/80 shadow-inner p-4">
                      {activeFeature.previewContent}
                    </div>
                  )}
                </div>

                {/* CTA footer */}
                <div className="px-6 py-4 bg-white border-t border-gray-100 flex items-center justify-between">
                  <p className="text-xs text-gray-400">
                    Log in to your school's account to see this in action.
                  </p>
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
                    onClick={() => setActiveFeature(null)}
                  >
                    Sign in →
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Data & Trust */}
      <section className="bg-slate-50 border-y border-gray-100 py-20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-900 text-center mb-4">
            Built with trust at the centre
          </h2>
          <p className="text-center text-gray-500 mb-12 text-sm">
            Designed for schools that take data protection seriously.{' '}
            <Link to="/data-security" className="text-brand-600 hover:underline">
              Read the full security page →
            </Link>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {trustItems.map((item) => (
              <div key={item.title} className="text-center px-4">
                <div className="text-4xl mb-3">{item.icon}</div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 border-b border-gray-100">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-900 text-center mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-center text-gray-500 text-sm mb-12 max-w-xl mx-auto">
            One flat annual fee per school — no per-user charges, no hidden costs.
            Everything included.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <div className="rounded-2xl border-2 border-brand-200 bg-white p-8 text-center shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-600 mb-2">Primary &amp; smaller secondary</p>
              <div className="text-4xl font-extrabold text-brand-900 mb-1">£395</div>
              <div className="text-sm text-gray-500 mb-6">per year · up to 60 staff</div>
              <ul className="text-sm text-gray-600 text-left space-y-2 mb-6">
                {['Full review workflow', 'CPD log + evidence linking', 'Automated reminders', 'Pay progression report', 'PDF export', 'UK data storage + DPA'].map(f => (
                  <li key={f} className="flex items-center gap-2"><span className="text-brand-500 font-bold">✓</span>{f}</li>
                ))}
              </ul>
              <a href="mailto:hello@pmreview.co.uk?subject=PM Review enquiry" className="block w-full rounded-md bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 transition-colors text-center">Get in touch</a>
            </div>
            <div className="rounded-2xl border-2 border-gray-200 bg-white p-8 text-center shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">Larger secondary</p>
              <div className="text-4xl font-extrabold text-gray-900 mb-1">£595</div>
              <div className="text-sm text-gray-500 mb-6">per year · 61–200 staff</div>
              <ul className="text-sm text-gray-600 text-left space-y-2 mb-6">
                {['Everything in primary plan', 'Multiple staff categories', 'Support staff appraisals', 'Multi-cycle management', 'Priority support', 'Custom onboarding'].map(f => (
                  <li key={f} className="flex items-center gap-2"><span className="text-gray-400 font-bold">✓</span>{f}</li>
                ))}
              </ul>
              <a href="mailto:hello@pmreview.co.uk?subject=PM Review enquiry" className="block w-full rounded-md border border-brand-600 px-4 py-2.5 text-sm font-semibold text-brand-600 hover:bg-brand-50 transition-colors text-center">Get in touch</a>
            </div>
          </div>
          <p className="text-center text-xs text-gray-400 mt-8">200+ staff? <a href="mailto:hello@pmreview.co.uk?subject=PM Review — large school enquiry" className="text-brand-600 hover:underline">Get in touch for a custom quote.</a></p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-900 mb-4">
            Interested in finding out more?
          </h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            We work with a small number of schools and take the time to understand
            how each one works before building anything. Get in touch and we'll have
            a conversation — no obligation, no hard sell.
          </p>
          <a
            href="mailto:hello@pmreview.co.uk?subject=PM Review enquiry"
            className="inline-flex items-center justify-center rounded-md bg-brand-600 px-8 py-3 text-base font-semibold text-white hover:bg-brand-700 transition-colors shadow-sm"
          >
            Get in touch
          </a>
          <p className="mt-4 text-sm text-gray-400">
            hello@pmreview.co.uk · Response within 24 hours
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-10">
        <div className="mx-auto max-w-6xl px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-500">
            <span className="font-semibold text-brand-900">PM Review</span>
            {' '}· © {new Date().getFullYear()} PM Review · Built for UK schools
          </div>
          <div className="flex items-center gap-5 text-sm text-gray-500">
            <Link to="/login" className="hover:text-brand-600 hover:underline transition-colors">
              Sign in
            </Link>
            <Link to="/data-security" className="hover:text-brand-600 hover:underline transition-colors">
              Data &amp; Security
            </Link>
            <a
              href="mailto:hello@pmreview.co.uk"
              className="hover:text-brand-600 hover:underline transition-colors"
            >
              hello@pmreview.co.uk
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
